// AGC: FinancesPage — Unified finances page with Expenses, Revenues, and Summary tabs
// AGC: Shows total revenue, total expenses, and net balance in a summary header
// AGC: Date filter bar: 7D, 30D, YTD, All (default), Safra, Custom
// AGC: Safra modal: create/select safra with name, year, grain, date start, date finish
// AGC: Custom date modal: from/to date picker
// AGC: FAB button opens a bottom sheet to add new expense or revenue
// AGC: All data served from FinancialContext (localStorage cache) — no Firebase call on every open
// AGC: Background sync happens automatically when cache is stale (>5 min) or device goes online

import React, { useState, useMemo, useRef, useCallback } from 'react'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Plus,
  X,
  Trash2,
  ChevronLeft,
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  Fuel,
  Sprout,
  Bug,
  Wheat,
  Users,
  Wrench,
  Settings,
  Package,
  ShoppingCart,
  Tractor,
  Landmark,
  HandCoins,
  Home,
  HelpCircle,
  Filter,
  CalendarDays,
  Check,
  RefreshCw,
} from 'lucide-react'
import { Card } from '../shared'
import { formatCurrency, formatDate } from '@droidfarm/shared'
import type { Screen, ExpenseCategory, RevenueCategory, Safra, FinanceDateFilter } from '../../types'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { useHasPermission } from '../../hooks/useRBAC'
import { useDeleteConfirm, useFinancial } from '../../context'
import toast from 'react-hot-toast'

// AGC: Tab type for finances page — 'all' is default (combined view)
type FinancesTab = 'all' | 'expenses' | 'revenues' | 'summary'

interface FinancesPageProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

// AGC: Swipe-to-delete constants — matches TasksList swipe behaviour
const SWIPE_ACTION_WIDTH = 80
const SWIPE_THRESHOLD = 40

// AGC: Generic swipeable row — swipe left to reveal red Delete button (iOS/Android style)
interface SwipeableRowProps {
  id: string
  onDelete: (id: string) => void
  deleteLabel: string
  children: React.ReactNode
}

const SwipeableRow: React.FC<SwipeableRowProps> = ({ id, onDelete, deleteLabel, children }) => {
  const [offset, setOffset] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const touchStartX = React.useRef(0)
  const touchStartOffset = React.useRef(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartOffset.current = offset
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartX.current
    setOffset(Math.max(-SWIPE_ACTION_WIDTH, Math.min(0, touchStartOffset.current + dx)))
  }
  const handleTouchEnd = () => {
    setOffset(offset < -SWIPE_THRESHOLD ? -SWIPE_ACTION_WIDTH : 0)
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    touchStartX.current = e.clientX
    touchStartOffset.current = offset
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const handlePointerMove = (e: React.PointerEvent) => {
    const dx = e.clientX - touchStartX.current
    setOffset(Math.max(-SWIPE_ACTION_WIDTH, Math.min(0, touchStartOffset.current + dx)))
  }
  const handlePointerUp = () => {
    setOffset(offset < -SWIPE_THRESHOLD ? -SWIPE_ACTION_WIDTH : 0)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isDeleting) return
    setIsDeleting(true)
    onDelete(id)
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* AGC: Red delete strip — revealed when user swipes left */}
      <div
        className="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-red-500 text-white rounded-r-xl"
        style={{ width: SWIPE_ACTION_WIDTH }}
      >
        <button
          type="button"
          onClick={handleDelete}
          disabled={isDeleting}
          className="flex flex-col items-center justify-center gap-0.5 w-full h-full active:bg-red-600 disabled:opacity-70"
          aria-label={deleteLabel}
        >
          <Trash2 className="w-5 h-5" />
          <span className="text-[10px] font-semibold">{deleteLabel}</span>
        </button>
      </div>
      {/* AGC: Sliding card content */}
      <div
        className="relative transition-transform duration-150 ease-out touch-pan-y"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClick={(e) => {
          if (offset < 0) { e.preventDefault(); e.stopPropagation(); setOffset(0) }
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * AGC: Get icon for expense category
 */
const getExpenseCategoryIcon = (category?: ExpenseCategory) => {
  switch (category) {
    case 'fuel': return <Fuel className="w-4 h-4 text-orange-600" />
    case 'fertilizer': return <Sprout className="w-4 h-4 text-green-600" />
    case 'pesticide': return <Bug className="w-4 h-4 text-red-600" />
    case 'seeds': return <Wheat className="w-4 h-4 text-amber-600" />
    case 'labor': return <Users className="w-4 h-4 text-blue-600" />
    case 'equipment': return <Wrench className="w-4 h-4 text-gray-600" />
    case 'maintenance': return <Settings className="w-4 h-4 text-purple-600" />
    default: return <Package className="w-4 h-4 text-gray-500" />
  }
}

/**
 * AGC: Get icon for revenue category
 */
const getRevenueCategoryIcon = (category?: RevenueCategory) => {
  switch (category) {
    case 'grain_sale': return <Wheat className="w-4 h-4 text-amber-600" />
    case 'machinery_sale': return <Tractor className="w-4 h-4 text-blue-600" />
    case 'tool_sale': return <Wrench className="w-4 h-4 text-orange-600" />
    case 'agrochemical_sale': return <Bug className="w-4 h-4 text-red-600" />
    case 'stock_sale': return <Package className="w-4 h-4 text-cyan-600" />
    case 'animal_sale': return <ShoppingCart className="w-4 h-4 text-pink-600" />
    case 'asset_sale': return <ShoppingCart className="w-4 h-4 text-indigo-600" />
    case 'service': return <HandCoins className="w-4 h-4 text-green-600" />
    case 'subsidy': return <Landmark className="w-4 h-4 text-purple-600" />
    case 'rental': return <Home className="w-4 h-4 text-teal-600" />
    default: return <HelpCircle className="w-4 h-4 text-gray-500" />
  }
}

/**
 * AGC: Capitalize first letter of a string (preserve rest of case)
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * AGC: Format large currency values with K/M suffixes for compact display
 * AGC: Matches the formatLargeValue pattern used in Home.tsx dashboard
 */
const formatLargeValue = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 10000) {
    return `R$ ${(value / 1000).toFixed(0)}K`
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}K`
  }
  return formatCurrency(value)
}

/**
 * AGC: Check if a date string falls within a given range [from, to]
 * AGC: dateStr can be ISO string or yyyy-mm-dd
 */
const isDateInRange = (dateStr: string, from: Date, to: Date): boolean => {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return false
  // AGC: Normalize to start/end of day for inclusive comparison
  const dayStart = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const dayEnd = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999)
  return d >= dayStart && d <= dayEnd
}

/**
 * AGC: FinancesPage — Main unified finances page
 */
export const FinancesPage: React.FC<FinancesPageProps> = ({ onNavigate, onBack }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const canEditFinancial = useHasPermission('canEditFinancial')
  const { confirmBeforeDelete, requestConfirmDelete } = useDeleteConfirm()
  // AGC: Pull data and mutations from FinancialContext (offline-first, localStorage-backed)
  const {
    expenses,
    revenues,
    safras,
    isLoading,
    isSyncing,
    refreshFinances,
    deleteExpense: ctxDeleteExpense,
    deleteRevenue: ctxDeleteRevenue,
    addSafra: ctxAddSafra,
    deleteSafra: ctxDeleteSafra,
  } = useFinancial()

  // AGC: TanStack Query handles cache staleness automatically via staleTime (5 min)
  // AGC: No manual invalidation needed on mount — cached data renders instantly,
  // AGC: and refetch fires in background only when cache is actually stale

  // AGC: ── Pull-to-refresh state ──────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null)
  const touchStartY = useRef(0)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const PULL_THRESHOLD = 60

  const handlePullTouchStart = useCallback((e: React.TouchEvent) => {
    // AGC: Only start tracking if scrolled to top
    if (scrollRef.current && scrollRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY
    } else {
      touchStartY.current = 0
    }
  }, [])

  const handlePullTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === 0 || isRefreshing) return
    const dy = e.touches[0].clientY - touchStartY.current
    if (dy > 0 && scrollRef.current && scrollRef.current.scrollTop <= 0) {
      // AGC: Dampen pull distance for a rubber-band feel (50% of actual drag)
      setPullDistance(Math.min(dy * 0.5, 120))
    }
  }, [isRefreshing])

  const handlePullTouchEnd = useCallback(async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true)
      // AGC: Haptic feedback on trigger
      try {
        const { Capacitor } = await import('@capacitor/core')
        if (Capacitor.isNativePlatform()) {
          const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
          await Haptics.impact({ style: ImpactStyle.Medium })
        }
      } catch { /* AGC: Haptics not available, proceed silently */ }
      try {
        await refreshFinances()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
    touchStartY.current = 0
  }, [pullDistance, isRefreshing, refreshFinances])

  const [activeTab, setActiveTab] = useState<FinancesTab>('all')
  const [showAddSheet, setShowAddSheet] = useState(false)

  // AGC: Date filter state — default is 'all'
  const [dateFilter, setDateFilter] = useState<FinanceDateFilter>('all')
  const [customDateFrom, setCustomDateFrom] = useState<string>('')
  const [customDateTo, setCustomDateTo] = useState<string>('')
  const [showCustomDateModal, setShowCustomDateModal] = useState(false)

  // AGC: Safra UI state (selection, form, modal)
  const [selectedSafra, setSelectedSafra] = useState<Safra | null>(null)
  const [showSafraModal, setShowSafraModal] = useState(false)
  const [showNewSafraForm, setShowNewSafraForm] = useState(false)
  const [safraForm, setSafraForm] = useState({
    name: '',
    year: '',
    grain: '',
    dateStart: '',
    dateEnd: '',
  })
  const [isSavingSafra, setIsSavingSafra] = useState(false)

  // AGC: Compute the date range based on the active filter
  const dateRange = useMemo((): { from: Date; to: Date } | null => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)

    switch (dateFilter) {
      case '7d': {
        const from = new Date(now)
        from.setDate(from.getDate() - 7)
        return { from, to: today }
      }
      case '30d': {
        const from = new Date(now)
        from.setDate(from.getDate() - 30)
        return { from, to: today }
      }
      case 'ytd': {
        // AGC: Year to date — from January 1st of current year
        const from = new Date(now.getFullYear(), 0, 1)
        return { from, to: today }
      }
      case 'safra': {
        if (selectedSafra) {
          return {
            from: new Date(selectedSafra.dateStart),
            to: new Date(selectedSafra.dateEnd),
          }
        }
        return null // AGC: No safra selected, show all
      }
      case 'custom': {
        if (customDateFrom && customDateTo) {
          return {
            from: new Date(customDateFrom),
            to: new Date(customDateTo),
          }
        }
        return null // AGC: No custom range set, show all
      }
      case 'all':
      default:
        return null // AGC: null = no filtering, show all
    }
  }, [dateFilter, selectedSafra, customDateFrom, customDateTo])

  // AGC: Filter expenses and revenues by date range
  const filteredExpenses = useMemo(() => {
    if (!dateRange) return expenses
    return expenses.filter(e => isDateInRange(e.date, dateRange.from, dateRange.to))
  }, [expenses, dateRange])

  const filteredRevenues = useMemo(() => {
    if (!dateRange) return revenues
    return revenues.filter(r => isDateInRange(r.date, dateRange.from, dateRange.to))
  }, [revenues, dateRange])

  // AGC: Combined list — merges expenses and revenues, sorted by date descending (newest first)
  const combinedList = useMemo(() => {
    const expenseItems = filteredExpenses.map(e => ({ ...e, _type: 'expense' as const }))
    const revenueItems = filteredRevenues.map(r => ({ ...r, _type: 'revenue' as const }))
    // AGC: Sort by date descending (newest first), with createdAt as tiebreaker
    // AGC: when entries share the same date (e.g. all today = T12:00:00.000Z)
    return [...expenseItems, ...revenueItems].sort((a, b) => {
      const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime()
      if (dateDiff !== 0) return dateDiff
      const createdA = (a as Record<string, unknown>).createdAt as string | undefined
      const createdB = (b as Record<string, unknown>).createdAt as string | undefined
      if (createdB && createdA) return new Date(createdB).getTime() - new Date(createdA).getTime()
      return 0
    })
  }, [filteredExpenses, filteredRevenues])

  // AGC: Calculate totals from filtered data
  const totalExpenses = useMemo(
    () => filteredExpenses.reduce((sum, exp) => sum + (exp.value || 0), 0),
    [filteredExpenses]
  )
  const totalRevenues = useMemo(
    () => filteredRevenues.reduce((sum, rev) => sum + (rev.value || 0), 0),
    [filteredRevenues]
  )
  const netBalance = totalRevenues - totalExpenses

  // AGC: Perform expense delete via context (updates cache + Firebase)
  const doDeleteExpense = async (id: string) => {
    try {
      await ctxDeleteExpense(id)
      toast.success(t('financial.deleteExpense'), { icon: '🗑️', duration: 2000 })
    } catch (error) {
      console.error('AGC: Error deleting expense:', error)
      toast.error('Erro ao excluir despesa')
    }
  }

  // AGC: Handle delete expense — shows confirm modal when Settings > Segurança > Sistema is ON
  const handleDeleteExpense = (id: string) => {
    if (confirmBeforeDelete) {
      requestConfirmDelete({ onConfirm: () => doDeleteExpense(id) })
    } else {
      void doDeleteExpense(id)
    }
  }

  // AGC: Perform revenue delete via context (updates cache + Firebase)
  const doDeleteRevenue = async (id: string) => {
    try {
      await ctxDeleteRevenue(id)
      toast.success(t('financial.deleteRevenue'), { icon: '🗑️', duration: 2000 })
    } catch (error) {
      console.error('AGC: Error deleting revenue:', error)
      toast.error('Erro ao excluir receita')
    }
  }

  // AGC: Handle delete revenue — shows confirm modal when Settings > Segurança > Sistema is ON
  const handleDeleteRevenue = (id: string) => {
    if (confirmBeforeDelete) {
      requestConfirmDelete({ onConfirm: () => doDeleteRevenue(id) })
    } else {
      void doDeleteRevenue(id)
    }
  }

  // AGC: Handle filter chip click
  const handleFilterClick = (filter: FinanceDateFilter) => {
    if (filter === 'safra') {
      // AGC: Open safra modal to select or create a safra
      setShowSafraModal(true)
      return
    }
    if (filter === 'custom') {
      // AGC: Open custom date range modal
      setShowCustomDateModal(true)
      return
    }
    setDateFilter(filter)
    setSelectedSafra(null)
    setCustomDateFrom('')
    setCustomDateTo('')
  }

  // AGC: Select an existing safra for filtering
  const handleSelectSafra = (safra: Safra) => {
    setSelectedSafra(safra)
    setDateFilter('safra')
    setShowSafraModal(false)
    setShowNewSafraForm(false)
  }

  // AGC: Create a new safra via context (updates cache + Firebase)
  const handleCreateSafra = async () => {
    if (!safraForm.name || !safraForm.year || !safraForm.grain || !safraForm.dateStart || !safraForm.dateEnd) {
      toast.error(t('financial.errorCreatingSafra'))
      return
    }
    try {
      setIsSavingSafra(true)
      const newSafra = await ctxAddSafra(safraForm)
      toast.success(t('financial.safraCreated'), { icon: '🌾', duration: 2000 })
      // AGC: Auto-select the newly created safra
      setSelectedSafra(newSafra)
      setDateFilter('safra')
      setShowNewSafraForm(false)
      setShowSafraModal(false)
      setSafraForm({ name: '', year: '', grain: '', dateStart: '', dateEnd: '' })
    } catch (error) {
      console.error('AGC: Error creating safra:', error)
      toast.error(t('financial.errorCreatingSafra'))
    } finally {
      setIsSavingSafra(false)
    }
  }

  // AGC: Perform safra delete via context (updates cache + Firebase)
  const doDeleteSafra = async (safraId: string) => {
    try {
      await ctxDeleteSafra(safraId)
      if (selectedSafra?.id === safraId) {
        setSelectedSafra(null)
        setDateFilter('all')
      }
      toast.success(t('financial.safraDeleted'), { icon: '🗑️', duration: 2000 })
    } catch (error) {
      console.error('AGC: Error deleting safra:', error)
      toast.error('Erro ao excluir safra')
    }
  }

  // AGC: Handle delete safra — shows confirm modal when Settings > Segurança > Sistema is ON
  const handleDeleteSafra = (safraId: string) => {
    if (confirmBeforeDelete) {
      requestConfirmDelete({ onConfirm: () => doDeleteSafra(safraId) })
    } else {
      void doDeleteSafra(safraId)
    }
  }

  // AGC: Apply custom date range
  const handleApplyCustomDate = () => {
    if (customDateFrom && customDateTo) {
      setDateFilter('custom')
      setSelectedSafra(null)
      setShowCustomDateModal(false)
    }
  }

  // AGC: Clear custom date range
  const handleClearCustomDate = () => {
    setCustomDateFrom('')
    setCustomDateTo('')
    setDateFilter('all')
    setShowCustomDateModal(false)
  }

  // AGC: Filter chips config
  const filterChips: { id: FinanceDateFilter; labelKey: string }[] = [
    { id: '7d', labelKey: 'filter7d' },
    { id: '30d', labelKey: 'filter30d' },
    { id: 'ytd', labelKey: 'filterYTD' },
    { id: 'all', labelKey: 'filterAll' },
    { id: 'safra', labelKey: 'filterSafra' },
    { id: 'custom', labelKey: 'filterCustom' },
  ]

  // AGC: Active filter label for subtitle display
  const activeFilterLabel = useMemo(() => {
    if (dateFilter === 'safra' && selectedSafra) {
      return selectedSafra.name
    }
    if (dateFilter === 'custom' && customDateFrom && customDateTo) {
      return `${customDateFrom} → ${customDateTo}`
    }
    return null
  }, [dateFilter, selectedSafra, customDateFrom, customDateTo])

  return (
    <div
      ref={scrollRef}
      className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-24 overflow-y-auto"
      onTouchStart={handlePullTouchStart}
      onTouchMove={handlePullTouchMove}
      onTouchEnd={handlePullTouchEnd}
    >
      {/* AGC: Pull-to-refresh indicator — slides down from top */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden transition-all duration-200 bg-[#F4F5F7] dark:bg-slate-900"
          style={{ height: isRefreshing ? PULL_THRESHOLD : pullDistance }}
        >
          <RefreshCw
            className={`w-4 h-4 text-[#065F46] dark:text-emerald-400 transition-transform ${
              isRefreshing ? 'animate-spin' : ''
            } ${pullDistance >= PULL_THRESHOLD ? 'scale-110' : 'scale-90 opacity-60'}`}
          />
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {isRefreshing
              ? t('financial.syncing')
              : pullDistance >= PULL_THRESHOLD
                ? t('financial.releaseToRefresh')
                : t('financial.pullToRefresh')}
          </span>
        </div>
      )}

      {/* AGC: Top Bar */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
            {t('financial.finances')}
          </h1>
          {/* AGC: Subtle syncing indicator — shows only during background Firebase refresh */}
          {isSyncing && (
            <span className="ml-auto text-[10px] text-slate-400 dark:text-slate-500 animate-pulse">
              {t('financial.syncing')}
            </span>
          )}
        </div>
      </div>

      {/* AGC: Date Filter Bar */}
      <div className="px-4 pt-3 pb-1">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {filterChips.map((chip) => {
            const isActive = dateFilter === chip.id
            return (
              <button
                key={chip.id}
                onClick={() => handleFilterClick(chip.id)}
                className={`
                  whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border
                  ${isActive
                    ? 'bg-[#065F46] text-white border-[#065F46] shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-[#065F46] hover:text-[#065F46]'
                  }
                `}
              >
                {t(`financial.${chip.labelKey}`)}
              </button>
            )
          })}
        </div>

        {/* AGC: Active filter indicator (shows safra name or custom date range) */}
        {activeFilterLabel && (
          <div className="flex items-center gap-2 mt-1.5 px-1">
            <Filter className="w-3 h-3 text-[#065F46]" />
            <span className="text-xs text-[#065F46] dark:text-emerald-400 font-medium truncate">
              {t('financial.safraActiveFilter')} {activeFilterLabel}
            </span>
            <button
              onClick={() => {
                setDateFilter('all')
                setSelectedSafra(null)
                setCustomDateFrom('')
                setCustomDateTo('')
              }}
              className="ml-auto p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <X className="w-3 h-3 text-slate-400" />
            </button>
          </div>
        )}
      </div>

      {/* AGC: Summary Cards - Compact layout matching dashboard style */}
      <div className="px-4 pt-2 pb-2">
        {/* AGC: Revenue / Expense / Net row - using gap-2 for tighter spacing */}
        <div className="grid grid-cols-3 gap-2">
          {/* AGC: Revenue card - compact padding, larger numbers */}
          <Card padding="none" className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <div className="p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <TrendingUp className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                <p className="text-[9px] text-green-700 dark:text-green-300 font-semibold uppercase tracking-wider">
                  {t('financial.revenues')}
                </p>
              </div>
              <p className="text-base font-bold text-green-800 dark:text-green-200 tabular-nums truncate">
                {formatLargeValue(totalRevenues)}
              </p>
            </div>
          </Card>

          {/* AGC: Expense card - compact padding, larger numbers */}
          <Card padding="none" className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800">
            <div className="p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <TrendingDown className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                <p className="text-[9px] text-red-700 dark:text-red-300 font-semibold uppercase tracking-wider">
                  {t('financial.expenses')}
                </p>
              </div>
              <p className="text-base font-bold text-red-800 dark:text-red-200 tabular-nums truncate">
                {formatLargeValue(totalExpenses)}
              </p>
            </div>
          </Card>

          {/* AGC: Net balance card - compact padding, larger numbers */}
          <Card padding="none" className={`bg-gradient-to-br ${netBalance >= 0
            ? 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800'
            : 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800'
          }`}>
            <div className="p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <DollarSign className={`w-3.5 h-3.5 ${netBalance >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`} />
                <p className={`text-[9px] font-semibold uppercase tracking-wider ${netBalance >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-amber-700 dark:text-amber-300'}`}>
                  {t('financial.netBalance')}
                </p>
              </div>
              <p className={`text-base font-bold tabular-nums truncate ${netBalance >= 0 ? 'text-blue-800 dark:text-blue-200' : 'text-amber-800 dark:text-amber-200'}`}>
                {formatLargeValue(netBalance)}
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* AGC: Tab bar — All (default combined), Expenses, Revenues, Summary */}
      <div className="px-4 pb-2">
        <div className="flex bg-white dark:bg-slate-800 rounded-xl p-1 shadow-sm border border-slate-200 dark:border-slate-700">
          {(['all', 'expenses', 'revenues', 'summary'] as FinancesTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                flex-1 py-2.5 px-1.5 rounded-lg text-xs font-medium transition-all duration-200
                ${activeTab === tab
                  ? 'bg-[#065F46] text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }
              `}
            >
              {tab === 'all' && t('financial.tabAll')}
              {tab === 'expenses' && t('financial.expenses')}
              {tab === 'revenues' && t('financial.revenues')}
              {tab === 'summary' && t('financial.summary')}
            </button>
          ))}
        </div>
      </div>

      {/* AGC: Tab Content */}
      <div className="px-4 py-2">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">{t('financial.loading')}</p>
          </div>
        ) : (
          <>
            {/* AGC: All Tab — combined expenses + revenues sorted by date */}
            {activeTab === 'all' && (
              <div className="space-y-3">
                {combinedList.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      {dateFilter !== 'all' ? t('financial.noDataForPeriod') : t('financial.noExpenses')}
                    </p>
                  </div>
                ) : (
                  combinedList.map((item) => {
                    const isExpense = item._type === 'expense'
                    return (
                      <SwipeableRow
                        key={`${item._type}-${item.id}`}
                        id={item.id}
                        onDelete={isExpense ? handleDeleteExpense : handleDeleteRevenue}
                        deleteLabel={t('common.delete')}
                      >
                        <Card hover padding="none">
                          <div className="relative p-3">
                            {/* AGC: Username tag */}
                            {(item.createdByName || user?.displayName) && (
                              <span className={`absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium max-w-[90px] truncate z-10 ${
                                isExpense
                                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                  : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              }`}>
                                <Users className="w-2.5 h-2.5 flex-shrink-0" />
                                <span className="truncate">{item.createdByName || user?.displayName?.split(' ')[0] || ''}</span>
                              </span>
                            )}
                            <div className="flex items-center justify-between gap-2">
                              {/* AGC: Left side — icon + info */}
                              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isExpense ? 'bg-red-50 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/30'
                                }`}>
                                  {isExpense
                                    ? getExpenseCategoryIcon((item as typeof filteredExpenses[0]).category)
                                    : getRevenueCategoryIcon((item as typeof filteredRevenues[0]).category)
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate pr-20">
                                    {capitalizeFirstLetter(item.title)}
                                    {/* AGC: Show quantity next to title for quantifiable items */}
                                    {item.itemQuantity != null && item.itemQuantity > 0 && (
                                      <span className="ml-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                                        Qty: {item.itemQuantity}{item.itemUnit && item.itemUnit !== 'un' ? ` ${item.itemUnit}` : ''}
                                      </span>
                                    )}
                                  </h3>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {item.category && (
                                      <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                        {isExpense
                                          ? t(`financial.categories.${item.category}`)
                                          : t(`financial.revenueCategories.${
                                              item.category === 'grain_sale' ? 'grainSale' :
                                              item.category === 'machinery_sale' ? 'machinerySale' :
                                              item.category === 'tool_sale' ? 'toolSale' :
                                              item.category === 'agrochemical_sale' ? 'agrochemicalSale' :
                                              item.category === 'stock_sale' ? 'stockSale' :
                                              item.category === 'animal_sale' ? 'animalSale' :
                                              item.category === 'asset_sale' ? 'assetSale' :
                                              item.category === 'fuel_sale' ? 'fuelSale' :
                                              item.category === 'fertilizer_sale' ? 'fertilizerSale' :
                                              item.category === 'pesticide_sale' ? 'pesticideSale' :
                                              item.category === 'seeds_sale' ? 'seedsSale' :
                                              item.category
                                            }`)
                                        }
                                      </span>
                                    )}
                                    <span className="text-gray-300 dark:text-gray-600">•</span>
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                      {formatDate(item.date)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* AGC: Right side — value only (delete is via swipe) */}
                              <p className={`text-sm font-bold tabular-nums flex-shrink-0 pr-1 ${
                                isExpense ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                              }`}>
                                {isExpense ? '-' : '+'}{formatLargeValue(item.value)}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </SwipeableRow>
                    )
                  })
                )}
              </div>
            )}

            {/* AGC: Expenses Tab */}
            {activeTab === 'expenses' && (
              <div className="space-y-3">
                {filteredExpenses.length === 0 ? (
                  <div className="text-center py-12">
                    <ArrowDownCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-1">
                      {dateFilter !== 'all' ? t('financial.noDataForPeriod') : t('financial.noExpenses')}
                    </p>
                    {dateFilter === 'all' && (
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        {t('financial.addExpense')}
                      </p>
                    )}
                  </div>
                ) : (
                  filteredExpenses.map((expense) => (
                    <SwipeableRow
                      key={expense.id}
                      id={expense.id}
                      onDelete={handleDeleteExpense}
                      deleteLabel={t('common.delete')}
                    >
                      <Card hover padding="none">
                        <div className="relative p-3">
                          {/* AGC: Username tag — fixed top-right corner of the card */}
                          {(expense.createdByName || user?.displayName) && (
                            <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-[9px] font-medium text-blue-600 dark:text-blue-400 max-w-[90px] truncate z-10">
                              <Users className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate">{expense.createdByName || user?.displayName?.split(' ')[0] || ''}</span>
                            </span>
                          )}
                          <div className="flex items-center justify-between gap-2">
                            {/* AGC: Left side - icon + info */}
                            <div className="flex items-center gap-2.5 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                {getExpenseCategoryIcon(expense.category)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate pr-20">
                                  {capitalizeFirstLetter(expense.title)}
                                  {/* AGC: Show quantity next to title for quantifiable items */}
                                  {expense.itemQuantity != null && expense.itemQuantity > 0 && (
                                    <span className="ml-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                                      Qty: {expense.itemQuantity}{expense.itemUnit && expense.itemUnit !== 'un' ? ` ${expense.itemUnit}` : ''}
                                    </span>
                                  )}
                                </h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {expense.category && (
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                      {t(`financial.categories.${expense.category}`)}
                                    </span>
                                  )}
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                    {formatDate(expense.date)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* AGC: Right side - value only (delete is via swipe) */}
                            <p className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums pr-1">
                              -{formatLargeValue(expense.value)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </SwipeableRow>
                  ))
                )}
              </div>
            )}

            {/* AGC: Revenues Tab */}
            {activeTab === 'revenues' && (
              <div className="space-y-3">
                {filteredRevenues.length === 0 ? (
                  <div className="text-center py-12">
                    <ArrowUpCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-1">
                      {dateFilter !== 'all' ? t('financial.noDataForPeriod') : t('financial.noRevenues')}
                    </p>
                    {dateFilter === 'all' && (
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        {t('financial.addRevenue')}
                      </p>
                    )}
                  </div>
                ) : (
                  filteredRevenues.map((revenue) => (
                    <SwipeableRow
                      key={revenue.id}
                      id={revenue.id}
                      onDelete={handleDeleteRevenue}
                      deleteLabel={t('common.delete')}
                    >
                      <Card hover padding="none">
                        <div className="relative p-3">
                          {/* AGC: Username tag — fixed top-right corner of the card */}
                          {(revenue.createdByName || user?.displayName) && (
                            <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30 text-[9px] font-medium text-green-600 dark:text-green-400 max-w-[90px] truncate z-10">
                              <Users className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate">{revenue.createdByName || user?.displayName?.split(' ')[0] || ''}</span>
                            </span>
                          )}
                          <div className="flex items-center justify-between gap-2">
                            {/* AGC: Left side - icon + info */}
                            <div className="flex items-center gap-2.5 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                {getRevenueCategoryIcon(revenue.category)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate pr-20">
                                  {capitalizeFirstLetter(revenue.title)}
                                  {/* AGC: Show quantity next to title for quantifiable items */}
                                  {revenue.itemQuantity != null && revenue.itemQuantity > 0 && (
                                    <span className="ml-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                                      Qty: {revenue.itemQuantity}{revenue.itemUnit && revenue.itemUnit !== 'un' ? ` ${revenue.itemUnit}` : ''}
                                    </span>
                                  )}
                                </h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {revenue.category && (
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                      {t(`financial.revenueCategories.${
                                        revenue.category === 'grain_sale' ? 'grainSale' :
                                        revenue.category === 'machinery_sale' ? 'machinerySale' :
                                        revenue.category === 'tool_sale' ? 'toolSale' :
                                        revenue.category === 'agrochemical_sale' ? 'agrochemicalSale' :
                                        revenue.category === 'stock_sale' ? 'stockSale' :
                                        revenue.category === 'animal_sale' ? 'animalSale' :
                                        revenue.category === 'asset_sale' ? 'assetSale' :
                                        revenue.category === 'fuel_sale' ? 'fuelSale' :
                                        revenue.category === 'fertilizer_sale' ? 'fertilizerSale' :
                                        revenue.category === 'pesticide_sale' ? 'pesticideSale' :
                                        revenue.category === 'seeds_sale' ? 'seedsSale' :
                                        revenue.category
                                      }`)}
                                    </span>
                                  )}
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                    {formatDate(revenue.date)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* AGC: Right side - value only (delete is via swipe) */}
                            <p className="text-sm font-bold text-green-600 dark:text-green-400 tabular-nums pr-1">
                              +{formatLargeValue(revenue.value)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </SwipeableRow>
                  ))
                )}
              </div>
            )}

            {/* AGC: Summary Tab - Matching dashboard layout */}
            {activeTab === 'summary' && (
              <div className="space-y-3">
                {/* AGC: Monthly overview card */}
                <Card padding="none">
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-[#065F46]" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('financial.summary')}
                      </h3>
                    </div>

                    <div className="space-y-2">
                      {/* AGC: Revenue total */}
                      <div className="flex items-center justify-between py-2 px-2.5 rounded-lg bg-green-50/50 dark:bg-green-900/10 border-b border-green-100 dark:border-green-800/30">
                        <div className="flex items-center gap-2 min-w-0">
                          <TrendingUp className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{t('financial.totalRevenues')}</span>
                        </div>
                        <span className="text-sm font-bold text-green-600 dark:text-green-400 tabular-nums flex-shrink-0">
                          +{formatLargeValue(totalRevenues)}
                        </span>
                      </div>

                      {/* AGC: Expense total */}
                      <div className="flex items-center justify-between py-2 px-2.5 rounded-lg bg-red-50/50 dark:bg-red-900/10 border-b border-red-100 dark:border-red-800/30">
                        <div className="flex items-center gap-2 min-w-0">
                          <TrendingDown className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{t('financial.totalExpenses')}</span>
                        </div>
                        <span className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums flex-shrink-0">
                          -{formatLargeValue(totalExpenses)}
                        </span>
                      </div>

                      {/* AGC: Net balance */}
                      <div className={`flex items-center justify-between py-2.5 px-2.5 rounded-lg ${
                        netBalance >= 0 
                          ? 'bg-blue-50/50 dark:bg-blue-900/10' 
                          : 'bg-amber-50/50 dark:bg-amber-900/10'
                      }`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <DollarSign className={`w-3.5 h-3.5 flex-shrink-0 ${netBalance >= 0 ? 'text-blue-600' : 'text-amber-600'}`} />
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{t('financial.netBalance')}</span>
                        </div>
                        <span className={`text-base font-bold tabular-nums flex-shrink-0 ${netBalance >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {formatLargeValue(netBalance)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* AGC: Counts - compact 2-column grid */}
                <div className="grid grid-cols-2 gap-2">
                  <Card padding="none" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                    <div className="p-3 text-center">
                      <p className="text-xl font-bold text-red-700 dark:text-red-300 tabular-nums">{filteredExpenses.length}</p>
                      <p className="text-[10px] text-red-600 dark:text-red-400 font-medium uppercase tracking-wide mt-0.5">{t('financial.expenses')}</p>
                    </div>
                  </Card>
                  <Card padding="none" className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <div className="p-3 text-center">
                      <p className="text-xl font-bold text-green-700 dark:text-green-300 tabular-nums">{filteredRevenues.length}</p>
                      <p className="text-[10px] text-green-600 dark:text-green-400 font-medium uppercase tracking-wide mt-0.5">{t('financial.revenues')}</p>
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* AGC: FAB — Add new expense or revenue; only if canEditFinancial */}
      {canEditFinancial && <button
        onClick={() => setShowAddSheet(true)}
        className="fixed right-4 w-14 h-14 bg-[#065F46] hover:bg-[#054c38] text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-30"
        style={{ bottom: 'calc(64px + max(env(safe-area-inset-bottom), 8px) + 12px)' }}
        aria-label={t('financial.addExpenseOrRevenue')}
      >
        <Plus className="w-6 h-6" />
      </button>}

      {/* AGC: Bottom Sheet — Choose Expense or Revenue */}
      {showAddSheet && (
        <>
          {/* AGC: Overlay */}
          <div
            className="fixed inset-0 bg-black/40 z-[90]"
            onClick={() => setShowAddSheet(false)}
          />
          {/* AGC: Sheet — positioned above BottomNav so nav stays visible */}
          <div
            className="fixed left-0 right-0 z-[90] bg-white dark:bg-slate-800 rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom duration-300"
            style={{ bottom: 'calc(64px + max(env(safe-area-inset-bottom), 8px))' }}
          >
            <div className="p-6">
              {/* AGC: Handle bar */}
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-4" />

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('financial.addExpenseOrRevenue')}
                </h3>
                <button
                  onClick={() => setShowAddSheet(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3">
                {/* AGC: Add Expense */}
                <button
                  onClick={() => {
                    setShowAddSheet(false)
                    onNavigate('new-expense')
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                    <ArrowDownCircle className="w-6 h-6 text-red-600 dark:text-red-300" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-red-800 dark:text-red-200">{t('financial.newExpense')}</p>
                    <p className="text-sm text-red-600 dark:text-red-400">{t('financial.expenses')}</p>
                  </div>
                </button>

                {/* AGC: Add Revenue */}
                <button
                  onClick={() => {
                    setShowAddSheet(false)
                    onNavigate('new-revenue')
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                    <ArrowUpCircle className="w-6 h-6 text-green-600 dark:text-green-300" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-green-800 dark:text-green-200">{t('financial.newRevenue')}</p>
                    <p className="text-sm text-green-600 dark:text-green-400">{t('financial.revenues')}</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* AGC: ============================================ */}
      {/* AGC: SAFRA MODAL — Select or Create a Crop Season */}
      {/* AGC: ============================================ */}
      {showSafraModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[90]"
            onClick={() => { setShowSafraModal(false); setShowNewSafraForm(false) }}
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
              {/* AGC: Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Wheat className="w-5 h-5 text-[#065F46]" />
                  {showNewSafraForm ? t('financial.newSafra') : t('financial.selectSafra')}
                </h3>
                <button
                  onClick={() => { setShowSafraModal(false); setShowNewSafraForm(false) }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* AGC: Modal Body — scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
                {showNewSafraForm ? (
                  // AGC: New Safra Form
                  <div className="space-y-4">
                    {/* AGC: Safra Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('financial.safraName')}
                      </label>
                      <input
                        type="text"
                        value={safraForm.name}
                        onChange={(e) => setSafraForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('financial.safraNamePlaceholder')}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                      />
                    </div>

                    {/* AGC: Safra Year */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('financial.safraYear')}
                      </label>
                      <input
                        type="text"
                        value={safraForm.year}
                        onChange={(e) => setSafraForm(prev => ({ ...prev, year: e.target.value }))}
                        placeholder={t('financial.safraYearPlaceholder')}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                      />
                    </div>

                    {/* AGC: Grain / Crop */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('financial.safraGrain')}
                      </label>
                      <input
                        type="text"
                        value={safraForm.grain}
                        onChange={(e) => setSafraForm(prev => ({ ...prev, grain: e.target.value }))}
                        placeholder={t('financial.safraGrainPlaceholder')}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                      />
                    </div>

                    {/* AGC: Date Start */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('financial.safraDateStart')}
                      </label>
                      <input
                        type="date"
                        value={safraForm.dateStart}
                        onChange={(e) => setSafraForm(prev => ({ ...prev, dateStart: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                      />
                    </div>

                    {/* AGC: Date End */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('financial.safraDateEnd')}
                      </label>
                      <input
                        type="date"
                        value={safraForm.dateEnd}
                        onChange={(e) => setSafraForm(prev => ({ ...prev, dateEnd: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                ) : (
                  // AGC: Safra List
                  <div className="space-y-3">
                    {safras.length === 0 ? (
                      <div className="text-center py-8">
                        <Wheat className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400 mb-1">{t('financial.noSafras')}</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          {t('financial.createFirstSafra')}
                        </p>
                      </div>
                    ) : (
                      safras.map((safra) => {
                        const isSelected = selectedSafra?.id === safra.id && dateFilter === 'safra'
                        return (
                          <button
                            key={safra.id}
                            onClick={() => handleSelectSafra(safra)}
                            className={`w-full text-left p-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'border-[#065F46] bg-emerald-50 dark:bg-emerald-900/20'
                                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700/50 hover:border-[#065F46]/50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {isSelected && <Check className="w-4 h-4 text-[#065F46] flex-shrink-0" />}
                                  <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                    {safra.name}
                                  </h4>
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {safra.grain} • {safra.year}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <CalendarDays className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {safra.dateStart} → {safra.dateEnd}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteSafra(safra.id)
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors flex-shrink-0"
                                aria-label={t('financial.deleteSafra')}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </button>
                        )
                      })
                    )}
                  </div>
                )}
              </div>

              {/* AGC: Modal Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                {showNewSafraForm ? (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowNewSafraForm(false)}
                      className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      {t('financial.clearFilter')}
                    </button>
                    <button
                      onClick={handleCreateSafra}
                      disabled={isSavingSafra || !safraForm.name || !safraForm.year || !safraForm.grain || !safraForm.dateStart || !safraForm.dateEnd}
                      className="flex-1 py-2.5 rounded-xl bg-[#065F46] text-white text-sm font-medium hover:bg-[#054c38] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingSafra ? t('financial.saving') : t('financial.saveSafra')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewSafraForm(true)}
                    className="w-full py-2.5 rounded-xl bg-[#065F46] text-white text-sm font-medium hover:bg-[#054c38] transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    {t('financial.newSafra')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* AGC: ============================================ */}
      {/* AGC: CUSTOM DATE RANGE MODAL                      */}
      {/* AGC: ============================================ */}
      {showCustomDateModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[90]"
            onClick={() => setShowCustomDateModal(false)}
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              {/* AGC: Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-[#065F46]" />
                  {t('financial.filterCustom')}
                </h3>
                <button
                  onClick={() => setShowCustomDateModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* AGC: Modal Body */}
              <div className="p-4 space-y-4">
                {/* AGC: From date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('financial.customDateFrom')}
                  </label>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                  />
                </div>

                {/* AGC: To date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('financial.customDateTo')}
                  </label>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#065F46] focus:border-transparent outline-none"
                  />
                </div>
              </div>

              {/* AGC: Modal Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button
                  onClick={handleClearCustomDate}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {t('financial.clearFilter')}
                </button>
                <button
                  onClick={handleApplyCustomDate}
                  disabled={!customDateFrom || !customDateTo}
                  className="flex-1 py-2.5 rounded-xl bg-[#065F46] text-white text-sm font-medium hover:bg-[#054c38] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('financial.applyFilter')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default FinancesPage
